<template>
  <v-dialog
    hide-overlay
    transition="none"
    v-model="internal"
    eager
    width="750"
    content-class="spotlight-wrap"
  >
    <liquid-glass-effect class="spotlight-shell" width="41.5rem" border-radius="18px">
      <!-- ── Input row ── -->
      <div class="spotlight-row">
        <transition name="icon-fade">
          <v-icon v-if="activeIcon" :key="'m-' + activeIcon" class="row-icon row-icon--match">
            {{ activeIcon }}
          </v-icon>
          <v-icon v-else key="srch" class="row-icon">mdi-magnify</v-icon>
        </transition>

        <input
          ref="nativeInput"
          class="spotlight-input"
          autocomplete="off"
          spellcheck="false"
          :placeholder="$t('searchSpotLight.productSearch')"
          :value="searchProduct"
          @input="onNativeInput"
          @keydown.enter.prevent="handleEnter"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
          @keydown.tab.prevent="acceptAutocomplete"
          @keydown.right="onRightArrow"
          @keydown.backspace="onDelete"
          @keydown.delete="onDelete"
          @keydown.esc="eraseInput"
        />
      </div>

      <!-- ── Expandable body: tags + divider + results ── -->
      <transition name="body-drop">
        <div class="spotlight-body" v-if="showBody">
          <!-- Tags row: descriptors of the top result -->
          <transition name="tags-fade">
            <div class="spotlight-tags" v-if="activeTags.length">
              <span v-for="tag in activeTags" :key="tag" class="spotlight-tag">
                {{ tag }}
              </span>
            </div>
          </transition>

          <div class="spotlight-sep" />

          <!-- Results list -->
          <div class="spotlight-list" ref="resultsList">
            <div
              v-for="(item, i) in filteredOptions"
              :key="item.name"
              class="spotlight-item"
              :class="{ 'spotlight-item--active': highlightedIndex === i }"
              @mouseenter="highlightedIndex = i"
              @click="handleNextPage(item.name)"
            >
              <v-icon class="item-icon">{{ item.icon }}</v-icon>
              <div class="item-text">
                <span class="item-name">{{ item.name }}</span>
                <span v-if="item.tags && item.tags.length" class="item-sub">
                  {{ item.tags.slice(0, 3).join(' · ') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </liquid-glass-effect>
  </v-dialog>
</template>

<script>
import { useCoreStore } from "@/store/coreStore";
import { mapStores } from "pinia";
import LiquidGlassEffect from "../layout/ui/LiquidGlassEffect.vue";

export default {
  components: { LiquidGlassEffect },
  props: {
    value: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchProduct: "",
      highlightedIndex: 0,
      userNavigated: false,
      typedText: "",
      isAutocompleting: false,
      isDeleting: false,
    };
  },
  watch: {
    internal(val) {
      if (val) {
        this.highlightedIndex = 0;
        this.focusAutocomplete();
      }
    },
    selectedCard(card) {
      if (card) {
        this.coreStore.saveSelectedProduct({
          id: card.id,
          name: card.name,
          path: card.link,
          value: card.value,
        });
      }
    },
  },
  computed: {
    internal: {
      get() { return this.value },
      set(v) {
        this.$emit("input", v);
        this.searchProduct = "";
        this.typedText = "";
      },
    },
    showBody() { return this.typedText.length > 0 },
    ...mapStores(useCoreStore),
    selectedCard() {
      const fav = this.coreStore.getSelectedProduct?.name?.toLowerCase?.()?.trim()
      if (!fav) return null
      return this.coreStore.contentCard.find(c => c.name.toLowerCase().includes(fav)) ?? null
    },
    allSearchItems() {
      const cards = this.coreStore.enabledCards.map(c => ({
        name: c.name,
        icon: c.icon,
        tags: c.tags || [],
        link: c.link,
      }))
      const tabs = this.coreStore.enabledTabs.map(t => ({
        name: t.label,
        icon: t.icon,
        tags: t.tags || [],
        link: t.link,
      }))
      return [...cards, ...tabs]
    },
    activeIcon() {
      return this.typedText ? (this.filteredOptions[0]?.icon ?? null) : null
    },
    activeTags() {
      return this.typedText ? (this.filteredOptions[0]?.tags ?? []) : []
    },
    filteredOptions() {
      const term = this.typedText.toLowerCase().trim()
      if (!term) return this.allSearchItems

      return this.allSearchItems
        .filter(item => {
          const name = item.name.toLowerCase()
          return name.includes(term) || item.tags.some(t => t.toLowerCase().includes(term))
        })
        .sort((a, b) => {
          const diff = this.scoreItem(b, term) - this.scoreItem(a, term)
          return diff !== 0 ? diff : a.name.localeCompare(b.name)
        })
    },
  },
  methods: {
    focusAutocomplete(retries = 8) {
      this.$nextTick(() => {
        const input = this.$refs.nativeInput;
        if (input) {
          input.focus();
          return;
        }
        if (retries > 0) {
          setTimeout(() => this.focusAutocomplete(retries - 1), 50);
        }
      });
    },
    onNativeInput(e) {
      if (this.isAutocompleting) return;
      const val = e.target.value;
      this.typedText = val;
      this.searchProduct = val;
      this.userNavigated = false;
      this.highlightedIndex = 0;
      if (!this.isDeleting) {
        this.$nextTick(() => this.applyTypeahead());
      }
    },
    applyTypeahead() {
      if (this.isDeleting || !this.typedText) return;

      const match = this.filteredOptions.find((item) =>
        item.name.toLowerCase().startsWith(this.typedText.toLowerCase())
      );

      if (!match || match.name.toLowerCase() === this.typedText.toLowerCase())
        return;

      const completion =
        this.typedText + match.name.slice(this.typedText.length);
      this.isAutocompleting = true;
      this.searchProduct = completion;

      this.$nextTick(() => {
        const input = this.$refs.nativeInput;
        if (input) {
          input.value = completion;
          input.setSelectionRange(this.typedText.length, completion.length);
        }
        this.isAutocompleting = false;
      });
    },
    acceptAutocomplete() {
      const input = this.$refs.nativeInput;
      if (!input) return;
      if (input.selectionEnd > input.selectionStart) {
        const fullText = input.value;
        this.typedText = fullText;
        this.searchProduct = fullText;
        this.$nextTick(() => {
          input.setSelectionRange(fullText.length, fullText.length);
        });
      }
    },
    onRightArrow() {
      const input = this.$refs.nativeInput;
      if (!input) return;
      if (input.selectionEnd > input.selectionStart) {
        this.acceptAutocomplete();
      }
    },
    navigateDown() {
      this.userNavigated = true;
      this.highlightedIndex = Math.min(
        this.highlightedIndex + 1,
        this.filteredOptions.length - 1
      );
      this.scrollToHighlighted();
    },
    navigateUp() {
      this.userNavigated = true;
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
      this.scrollToHighlighted();
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        const item = list?.children[this.highlightedIndex];
        item?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    onDelete() {
      this.isDeleting = true;
      setTimeout(() => {
        this.isDeleting = false;
      }, 0);
    },
    scoreItem(item, term) {
      const name = item.name.toLowerCase()
      let score = 0
      if (name.startsWith(term)) score += 3
      if (name.includes(term)) score += 1
      if (item.tags.some(t => t.toLowerCase().includes(term))) score += 2
      return score
    },
    handleNextPage(value) {
      const link = this.findLink(value);
      if (this.$route.path === link) { this.eraseInput(); return }
      this.internal = false;
      this.$router.push({ path: link });
    },
    findLink(value) {
      const key = value.toLowerCase()
      const item = this.allSearchItems.find(i => i.name.toLowerCase().startsWith(key))
      return item?.link ?? '/home'
    },
    handleEnter() {
      if (!this.filteredOptions.length) return;
      const selected = this.userNavigated
        ? this.filteredOptions[this.highlightedIndex]
        : this.filteredOptions[0];
      if (selected) this.handleNextPage(selected.name);
    },
    eraseInput() {
      this.searchProduct = "";
      this.typedText = "";
      this.highlightedIndex = 0;
      this.userNavigated = false;
      this.$refs.nativeInput?.blur();
      this.internal = false;
    },
    autoFillChip(name) {
      this.handleNextPage(name);
    },
  },
};
</script>

<style>
/* Override v-dialog container so it doesn't interfere */
.spotlight-wrap {
  box-shadow: none !important;
  background: transparent !important;
  border-radius: 0 !important;
  overflow: visible !important;
  max-height: none !important;
}
</style>

<style scoped>
/* ── Unified glass shell ─────────────────────────────────────────────────── */
.spotlight-shell {
  position: fixed;
  top: 32%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.08);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.2),
    0 24px 64px rgba(0, 0, 0, 0.28);
  overflow: hidden;
  z-index: 500;
}

/* ── Search input row ────────────────────────────────────────────────────── */
.spotlight-row {
  display: flex;
  align-items: center;
  height: 5rem;
  padding: 0 1.25rem;
  gap: 0.8rem;
  position: relative;
}

.row-icon {
  font-size: 1.25rem;
  opacity: 0.5;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
}

.row-icon--match {
  opacity: 0.8;
}

.spotlight-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1.1rem;
  font-weight: 300;
  color: inherit;
  caret-color: #febd0b;
  letter-spacing: 0.01em;
  min-width: 0;
}

.spotlight-input::placeholder {
  color: #ffbf00;
  font-weight: 300;
  opacity: 0.8;
}

.spotlight-input::selection {
  background: rgba(180, 180, 180, 0.4);
  color: rgba(200, 200, 200, 0.9);
}

.spotlight-body {
  overflow: hidden;
}

.spotlight-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  padding: 0.55rem 1.25rem 0.65rem;
}

.spotlight-tag {
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.7);
  user-select: none;
}

/* ── Separator ───────────────────────────────────────────────────────────── */
.spotlight-sep {
  height: 1px;
  margin: 0 1rem;
  background: rgba(255, 255, 255, 0.1);
}

/* ── Results list ────────────────────────────────────────────────────────── */
.spotlight-list {
  max-height: 18rem;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 0.4rem 0.5rem 0.6rem;
}

.spotlight-list::-webkit-scrollbar {
  display: none;
}

.spotlight-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.1s ease;
}

.spotlight-item--active {
  background: rgba(255, 255, 255, 0.3);
}

.item-icon {
  font-size: 1.2rem;
  opacity: 0.8;
  flex-shrink: 0;
}

.item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.item-name {
  font-size: 0.95rem;
  font-weight: 300;
  letter-spacing: 0.01em;
}

.item-sub {
  font-size: 0.72rem;
  font-weight: 300;
  opacity: 0.45;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Body drop-in animation ─────────────────────────────────────────────── */
.body-drop-enter-active {
  transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
}
.body-drop-leave-active {
  transition: max-height 0.22s ease, opacity 0.15s ease;
}
.body-drop-enter,
.body-drop-leave-to {
  max-height: 0;
  opacity: 0;
}
.body-drop-enter-to,
.body-drop-leave {
  max-height: 700px;
  opacity: 1;
}

/* ── Tags fade animation ─────────────────────────────────────────────────── */
.tags-fade-enter-active,
.tags-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.tags-fade-enter,
.tags-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Icon swap animation ─────────────────────────────────────────────────── */
.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
  position: absolute;
}
.icon-fade-enter,
.icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.7);
}
.icon-fade-enter-to {
  opacity: 0.8;
  transform: scale(1);
}
</style>
